import { memo } from 'react';
import classNames from 'classnames';
import makeStyles from '@material-ui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Switch, Checkbox, NumberInput } from 'components/Form/Inputs';
import useCurrencySymbol from 'hooks/redux/Brands/useCurrencySymbol';
import { grayColor } from 'assets/jss/material-dashboard-pro-react';

import { type Platform, type PlatformConfig } from './platforms';

const useStyles = makeStyles({
  tile: {
    border: `1px solid ${grayColor[11]}`,
    borderRadius: '6px',
    padding: '8px 16px 16px',
    height: '100%',
  },
  tileEnabled: {
    borderColor: grayColor[9],
  },
  tileHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  platformName: {
    fontWeight: 500,
  },
  overrideArea: {
    marginTop: '4px',
    paddingTop: '4px',
    borderTop: `1px dashed ${grayColor[11]}`,
  },
});

type PlatformTileProps = {
  platform: Platform;
  config: PlatformConfig;
  isPercent: boolean;
  isEdit: boolean;
  onChange: (platform: Platform, changes: Partial<PlatformConfig>) => void;
};

const PlatformTile = ({
  platform,
  config,
  isPercent,
  isEdit,
  onChange,
}: PlatformTileProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const currencySymbol = useCurrencySymbol();

  const discountAdornment = isPercent ? '%' : currencySymbol;

  return (
    <div
      className={classNames(classes.tile, {
        [classes.tileEnabled]: config.enabled,
      })}
    >
      <div className={classes.tileHeader}>
        <span className={classes.platformName}>
          {t(`discountCodes.platforms.${platform}`)}
        </span>
        <Switch
          value={config.enabled}
          onChange={enabled => onChange(platform, { enabled })}
          disabled={isEdit}
        />
      </div>
      {config.enabled && (
        <div className={classes.overrideArea}>
          <Checkbox
            value={config.overrideDiscount}
            onChange={overrideDiscount =>
              onChange(platform, { overrideDiscount })
            }
            label={t('discountCodes.platforms.overrideDiscount')}
            disabled={isEdit}
          />
          {config.overrideDiscount && (
            <NumberInput
              label={t('discountCodes.platforms.discountValue')}
              name={`platform-discount-${platform}`}
              value={config.discount}
              onChange={discount => onChange(platform, { discount })}
              adornment={discountAdornment}
              disabled={isEdit}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default memo(PlatformTile);
